import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useState, useEffect } from 'react';
import { Typography, Button, Box, Paper, Container, LinearProgress, useTheme } from '@mui/material';
import { LanguageContext } from '../contexts/LanguageContext';
// Breathing phases
var Phase;
(function (Phase) {
    Phase[Phase["PREP"] = 0] = "PREP";
    Phase[Phase["INHALE"] = 1] = "INHALE";
    Phase[Phase["HOLD"] = 2] = "HOLD";
    Phase[Phase["EXHALE"] = 3] = "EXHALE";
})(Phase || (Phase = {}));
var BreathingExercise = function (_a) {
    var onProceed = _a.onProceed;
    var t = useContext(LanguageContext).t;
    var theme = useTheme();
    // States
    var _b = useState(Phase.PREP), phase = _b[0], setPhase = _b[1];
    var _c = useState(5), displayCountDown = _c[0], setDisplayCountDown = _c[1];
    var _d = useState(5), countdown = _d[0], setCountdown = _d[1]; // 5 second countdown before starting
    var _e = useState(0), cycleCount = _e[0], setCycleCount = _e[1];
    var _f = useState(150), timeLeft = _f[0], setTimeLeft = _f[1]; // 2.5 minutes (150 seconds)
    var _g = useState(false), showBanner = _g[0], setShowBanner = _g[1];
    var _h = useState(false), isHoldPhase = _h[0], setIsHoldPhase = _h[1]; // State to track HOLD phase
    var _j = useState(0), progress = _j[0], setProgress = _j[1]; // State to track animation progress (0-100)
    // Constants
    var INHALE_TIME = 4; // 4 seconds inhale
    var HOLD_TIME = 1; // 1 second hold
    var EXHALE_TIME = 4; // 4 seconds exhale
    var TOTAL_TIME = 150; // 2.5 minutes (150 seconds)
    // Effect for preparation countdown
    useEffect(function () {
        var timer;
        if (phase === Phase.PREP && countdown > 0) {
            timer = setTimeout(function () {
                setCountdown(function (prev) { return prev - 1; });
                setDisplayCountDown(function (prev) { return prev - 1; });
            }, 1000);
        }
        else if (phase === Phase.PREP && countdown === 0) {
            // When prep countdown completes, initialize inhale properly
            setCountdown(INHALE_TIME);
            setDisplayCountDown(0); // Start INHALE count at 0
            setPhase(Phase.INHALE);
            setProgress(0); // Reset animation progress
        }
        return function () { return clearTimeout(timer); };
    }, [phase, countdown, INHALE_TIME]);
    // Effect for breathing cycle
    useEffect(function () {
        var timer;
        if (phase !== Phase.PREP && timeLeft > 0) {
            // Main timing for phase transitions
            timer = setTimeout(function () {
                setTimeLeft(function (prev) { return prev - 1; });
                // Handle breathing phases
                if (phase === Phase.INHALE) {
                    if (countdown === 0) { // When countdown reaches 0, move to HOLD
                        setCountdown(HOLD_TIME);
                        setIsHoldPhase(true); // Enable HOLD phase display
                        setPhase(Phase.HOLD);
                        setProgress(100); // Keep progress at max for HOLD
                    }
                    else {
                        setCountdown(function (prev) { return prev - 1; });
                        // Set the progress first, then update the display count
                        // This ensures progress is already at target when count changes
                        var nextCount = displayCountDown + 1;
                        setProgress((nextCount / 4) * 100); // Update progress right away
                        setDisplayCountDown(nextCount); // Then update the display count
                    }
                }
                else if (phase === Phase.HOLD) {
                    if (countdown === 0) { // When countdown reaches 0, move to EXHALE
                        setCountdown(EXHALE_TIME);
                        setDisplayCountDown(4); // Start EXHALE count at 4
                        setIsHoldPhase(false); // Disable HOLD phase display
                        setPhase(Phase.EXHALE);
                        setProgress(100); // Start at max for EXHALE
                    }
                    else {
                        setCountdown(function (prev) { return prev - 1; });
                    }
                }
                else if (phase === Phase.EXHALE) {
                    if (countdown === 0) { // When countdown reaches 0, move to next cycle
                        setCycleCount(function (prev) { return prev + 1; });
                        setCountdown(INHALE_TIME);
                        setDisplayCountDown(0); // Start INHALE count at 0
                        setPhase(Phase.INHALE);
                        setProgress(0); // Reset progress for new INHALE
                    }
                    else {
                        setCountdown(function (prev) { return prev - 1; });
                        // Set the progress first, then update the display count
                        var nextCount = displayCountDown - 1;
                        setProgress((nextCount / 4) * 100); // Update progress right away
                        setDisplayCountDown(nextCount); // Then update the display count
                    }
                }
            }, 1000);
        }
        return function () {
            clearTimeout(timer);
        };
    }, [phase, countdown, timeLeft, INHALE_TIME, EXHALE_TIME]);
    // Effect to show banner after 3 cycles
    useEffect(function () {
        if (cycleCount >= 3) {
            setShowBanner(true);
        }
    }, [cycleCount]);
    // Initialize breathing cycle when transitioning to a new phase
    useEffect(function () {
        // Reset animation progress when phase changes
        if (phase === Phase.INHALE) {
            setProgress(0); // Start at 0% for inhale
        }
        else if (phase === Phase.HOLD) {
            setProgress(100); // Hold at 100%
        }
        else if (phase === Phase.EXHALE) {
            setProgress(100); // Start at 100% for exhale
        }
    }, [phase]);
    // Get phase text
    var getPhaseText = function () {
        switch (phase) {
            case Phase.PREP:
                return "".concat(t('breathingPrep'), " ").concat(displayCountDown);
            case Phase.INHALE:
                return t('inhale');
            case Phase.HOLD:
                return t('hold');
            case Phase.EXHALE:
                return t('exhale');
        }
    };
    // Calculate progress for the current phase - using the smoothed progress state
    var getPhaseProgress = function () {
        if (phase === Phase.PREP) {
            return ((5 - displayCountDown) / 5) * 100;
        }
        // For breathing phases, map directly to count values
        // INHALE: 0→0%, 1→25%, 2→50%, 3→75%, 4→100%
        // EXHALE: 4→100%, 3→75%, 2→50%, 1→25%, 0→0%
        return progress;
    };
    // Calculate overall progress
    var getOverallProgress = function () {
        return ((TOTAL_TIME - timeLeft) / TOTAL_TIME) * 100;
    };
    // Format time remaining as MM:SS
    var formatTimeLeft = function () {
        var minutes = Math.floor(timeLeft / 60);
        var seconds = timeLeft % 60;
        return "".concat(minutes, ":").concat(seconds < 10 ? '0' : '').concat(seconds);
    };
    return (_jsx(Container, { maxWidth: "sm", sx: {
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            py: 2
        }, children: _jsxs(Paper, { elevation: 3, sx: {
                p: { xs: 2, sm: 4 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                borderRadius: 2,
                bgcolor: 'background.paper',
                position: 'relative',
                overflow: 'hidden',
                width: '100%',
                minHeight: { xs: 'calc(100vh - 120px)', sm: 'auto' }
            }, children: [_jsx(Typography, { variant: "h4", component: "h1", gutterBottom: true, align: "center", children: t('breathingTitle') }), _jsxs(Box, { sx: { width: '100%', mt: 4 }, children: [_jsx(Typography, { variant: "h2", align: "center", sx: {
                                mb: 2,
                                fontWeight: 'bold',
                                color: theme.palette.primary.dark,
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                fontSize: { xs: '2.2rem', sm: '2.5rem', md: '3rem' }
                            }, children: getPhaseText() }), _jsx(LinearProgress, { variant: "determinate", value: getPhaseProgress(), sx: {
                                height: 12,
                                borderRadius: 6,
                                '& .MuiLinearProgress-bar': {
                                    background: "linear-gradient(to right, ".concat(theme.palette.primary.light, ", ").concat(theme.palette.primary.main, ")"),
                                }
                            } })] }), _jsx(Box, { sx: {
                        width: { xs: 220, sm: 240, md: 280 },
                        height: { xs: 220, sm: 240, md: 280 },
                        borderRadius: '50%',
                        border: "6px solid ".concat(theme.palette.primary.main),
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mt: 4,
                        // Scale based on the progress, which is now directly tied to count changes
                        transform: phase === Phase.INHALE
                            ? "scale(".concat(1 + (0.3 * progress / 100), ")")
                            : phase === Phase.EXHALE
                                ? "scale(".concat(1.3 - (0.3 * (100 - progress) / 100), ")")
                                : phase === Phase.HOLD
                                    ? 'scale(1.3)'
                                    : 'scale(1)',
                        // Use a full 1s transition to match the 1s count change
                        transition: 'transform 1s linear',
                        boxShadow: "0 0 20px ".concat(theme.palette.primary.light)
                    }, children: isHoldPhase ? (_jsx(Typography, { variant: "h3", align: "center", sx: {
                            fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                            fontWeight: 'bold',
                            color: theme.palette.primary.main
                        }, children: t('hold') })) : (_jsx(Typography, { variant: "h1", align: "center", sx: {
                            fontSize: { xs: '6rem', sm: '7rem', md: '8rem' },
                            fontWeight: 'bold',
                            color: theme.palette.primary.main
                        }, children: displayCountDown })) }), _jsxs(Box, { sx: { width: '100%', mt: 4, mb: 2 }, children: [_jsxs(Typography, { variant: "body2", align: "center", sx: { mb: 1 }, children: [t('timeLeft'), ": ", formatTimeLeft()] }), _jsx(LinearProgress, { variant: "determinate", value: getOverallProgress(), sx: { height: 6, borderRadius: 3 } })] }), showBanner && (_jsx(Box, { sx: {
                        position: 'relative',
                        width: '100%',
                        overflow: 'hidden',
                        mt: 2,
                        py: 1,
                        bgcolor: theme.palette.primary.main,
                        color: 'white',
                        borderRadius: 1,
                    }, children: _jsx(Box, { sx: {
                            whiteSpace: 'nowrap',
                            animation: 'banner-scroll 15s linear infinite',
                            "@keyframes banner-scroll": {
                                "0%": { transform: 'translateX(100%)' },
                                "100%": { transform: 'translateX(-100%)' }
                            }
                        }, children: _jsx(Typography, { variant: "subtitle1", sx: { fontWeight: 500 }, children: t('panicMessage') }) }) })), _jsx(Button, { variant: "contained", color: "primary", size: "large", onClick: onProceed, disabled: timeLeft > 0, sx: { mt: 3, py: 1.5, px: 4 }, children: t('proceedToGrounding') })] }) }));
};
export default BreathingExercise;
